/**
 * Notification Sounds & SW message bridge
 * ---------------------------------------
 * The service worker can't play audio directly, so it posts a message to
 * every open client when a push arrives. This module:
 *
 *   1. Registers a listener for those messages.
 *   2. Plays the right sound based on the `sound` key in the message.
 *   3. Falls back to synthesized tones (Web Audio) for events that don't
 *      have a bundled MP3, so the seller still gets an audible cue.
 *
 * Browsers block audio playback before any user interaction. We unlock the
 * audio context on the first click/tap so notifications can play sound
 * thereafter without re-prompting.
 */

const SOUND_FILES = {
  cash_register: '/sounds/new-order.mp3',
};

// Synthesized fallbacks for sounds we don't ship as MP3s. Each is a small
// burst designed to be distinguishable from the cash-register without
// sounding like a generic system beep.
const SYNTH_PATTERNS = {
  chime:     [{ f: 880, d: 0.10 }, { f: 1175, d: 0.16, delay: 0.10 }],                                   // 2-note up
  alert:     [{ f: 660, d: 0.12 }, { f: 440, d: 0.16, delay: 0.13 }, { f: 660, d: 0.12, delay: 0.30 }],  // 3-note urgent
  soft_ping: [{ f: 1320, d: 0.08 }, { f: 990, d: 0.12, delay: 0.10 }],                                   // soft down
};

let audioCtx = null;
let unlocked = false;
let mutedUntil = 0;       // server-side quiet hours could set this later
const audioCache = new Map();

function _ctx() {
  if (!audioCtx) {
    const AC = window.AudioContext || window.webkitAudioContext;
    if (AC) audioCtx = new AC();
  }
  return audioCtx;
}

/** Unlock audio on first user gesture. Browser policy. */
function _unlock() {
  if (unlocked) return;
  const ctx = _ctx();
  if (!ctx) return;
  if (ctx.state === 'suspended') ctx.resume().catch(() => {});
  // Pre-load the cash-register so the first ping is snappy
  Object.values(SOUND_FILES).forEach((url) => {
    if (audioCache.has(url)) return;
    const a = new Audio(url);
    a.preload = 'auto';
    a.load();
    audioCache.set(url, a);
  });
  unlocked = true;
}

function _playFile(url) {
  // Make a fresh Audio element each time so rapid sequential pushes don't
  // clip each other. Cache the decoded asset by URL.
  const a = new Audio(url);
  a.volume = 1.0;
  a.play().catch((err) => {
    // Autoplay blocked — likely no user gesture yet. Stay silent; the OS
    // notification's default sound still fires.
    console.debug('[notif-sound] playback blocked:', err.message);
  });
}

function _playSynth(pattern) {
  const ctx = _ctx();
  if (!ctx) return;
  if (ctx.state === 'suspended') ctx.resume().catch(() => {});
  const now = ctx.currentTime;
  pattern.forEach(({ f, d, delay = 0 }) => {
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.frequency.value = f;
    osc.type = 'sine';
    // ADSR-ish envelope to avoid click on start/stop
    gain.gain.setValueAtTime(0.0001, now + delay);
    gain.gain.exponentialRampToValueAtTime(0.18, now + delay + 0.015);
    gain.gain.exponentialRampToValueAtTime(0.0001, now + delay + d);
    osc.connect(gain).connect(ctx.destination);
    osc.start(now + delay);
    osc.stop(now + delay + d + 0.02);
  });
}

export function playNotificationSound(key) {
  if (Date.now() < mutedUntil) return;
  if (SOUND_FILES[key]) {
    _playFile(SOUND_FILES[key]);
  } else if (SYNTH_PATTERNS[key]) {
    _playSynth(SYNTH_PATTERNS[key]);
  } else {
    _playSynth(SYNTH_PATTERNS.soft_ping);
  }
}

/** Wire up the SW → client postMessage bridge. Call once on app boot. */
export function initNotificationSoundBridge() {
  if (typeof window === 'undefined' || !('serviceWorker' in navigator)) return;

  // Unlock on the first interaction. Once unlocked, listener self-removes.
  const unlockOnce = () => { _unlock(); };
  ['click', 'touchstart', 'keydown'].forEach((evt) =>
    window.addEventListener(evt, unlockOnce, { once: true, passive: true })
  );

  navigator.serviceWorker.addEventListener('message', (event) => {
    const msg = event.data;
    if (!msg || typeof msg !== 'object') return;
    if (msg.type !== 'aniyor:push' && msg.type !== 'aniyor:notification-click') return;
    // Only play if the tab is visible — the OS will handle background audio
    if (document.visibilityState === 'visible') {
      playNotificationSound(msg.sound);
    }
  });
}

/** Manually mute notification sounds for N minutes (e.g., "Do Not Disturb"). */
export function muteFor(minutes) {
  mutedUntil = Date.now() + minutes * 60 * 1000;
}
